import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContext';
import { updateUserProfile } from '../api'; // Supondo que esta função existe e atualiza todos os campos
import { useNotification } from '../contexts/NotificationContext';
import styles from './Profile.module.css'; // Reutilizando Profile.module.css

const PersonalInformationSection = () => {
  const { t } = useTranslation();
  const { user, setUser, fetchUserInfo } = useUser(); // setUser e fetchUserInfo para atualizar o contexto global
  const { addNotification } = useNotification();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    occupation: '', // Adicionar outros campos conforme necessário
    university: '',
    period: '',
    matricula: '',
    identifier_type: '',
    identifier_number: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        occupation: user.occupation || '',
        university: user.university || '',
        period: user.period || '',
        matricula: user.matricula || '',
        identifier_type: user.identifier_type || '',
        identifier_number: user.identifier_number || '',
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    try {
      // Apenas envia campos que foram alterados e são relevantes para o backend
      const dataToUpdate = {};
      if (formData.full_name !== user.full_name) dataToUpdate.full_name = formData.full_name;
      if (formData.email !== user.email) dataToUpdate.email = formData.email;
      if (formData.phone_number !== user.phone_number) dataToUpdate.phone_number = formData.phone_number;
      // Adicionar lógica para outros campos se eles forem editáveis
      // Ex: occupation, university, etc. Se esses não são editáveis aqui, não os inclua.

      if (Object.keys(dataToUpdate).length === 0) {
        addNotification(t('noChangesToSave'), 'info');
        setIsEditing(false);
        return;
      }

      const updatedUser = await updateUserProfile(dataToUpdate);
      setUser(updatedUser); // Atualiza o contexto global do usuário
      addNotification(t('profileUpdatedSuccess'), 'success');
      setIsEditing(false);
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      addNotification(error.message || t('errorUpdatingProfile'), 'error');
    }
  };

  const renderInfoField = (label, value, fieldName, type = "text", readOnly = !isEditing, placeholder = "") => (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}:</span>
      {isEditing && !readOnly ? (
        <input
          type={type}
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleInputChange}
          className={styles.infoInput}
          placeholder={placeholder}
        />
      ) : (
        <span className={styles.infoValue}>{value || '-'}</span>
      )}
    </div>
  );

  if (!user) return <p>{t('loadingUserInformation')}</p>;

  return (
    <div className="profile-section"> {/* Esta classe deve corresponder à .tabContent em Profile.module.css */}
      <h3 className={styles['tab-section-title']}>{t('personalInformation')}</h3>

      <div className={styles.personalInfoContainer}>
        {renderInfoField(t('fullName'), user.full_name, 'full_name', "text", !isEditing, t('fullNamePlaceholder'))}
        {renderInfoField(t('email'), user.email, 'email', "email", !isEditing, t('emailPlaceholder'))}
        {renderInfoField(t('phone'), user.phone_number, 'phone_number', "tel", !isEditing, t('phonePlaceholder'))}
        {renderInfoField(t('occupation'), user.occupation, 'occupation', "text", true)} {/* Ocupação não editável aqui */}

        {user.occupation === 'Estudante de Medicina' && (
          <>
            {renderInfoField(t('university'), user.university, 'university', "text", true)}
            {renderInfoField(t('period'), user.period, 'period', "text", true)}
            {renderInfoField(t('enrollment'), user.matricula, 'matricula', "text", true)}
          </>
        )}
        {user.occupation === 'Médico' && (
          <>
            {renderInfoField(t('identifier'), `${user.identifier_type || ''} ${user.identifier_number || ''}`.trim(), 'identifier', "text", true)}
          </>
        )}
      </div>

      <div className={styles.editControls}>
        {isEditing ? (
          <>
            <button onClick={handleSaveChanges} className={`${styles['button-base']} ${styles['button-primary']}`}>
              {t('saveChanges')}
            </button>
            <button onClick={() => {
              setIsEditing(false);
              // Reseta o formData para os valores originais do usuário ao cancelar
              if (user) {
                setFormData({
                  full_name: user.full_name || '',
                  email: user.email || '',
                  phone_number: user.phone_number || '',
                  occupation: user.occupation || '',
                  university: user.university || '',
                  period: user.period || '',
                  matricula: user.matricula || '',
                  identifier_type: user.identifier_type || '',
                  identifier_number: user.identifier_number || '',
                });
              }
            }} className={`${styles['button-base']} ${styles['button-neutral']}`}>
              {t('cancel')}
            </button>
          </>
        ) : (
          <button onClick={() => setIsEditing(true)} className={`${styles['button-base']} ${styles['button-secondary']}`}>
            {t('editInformation')}
          </button>
        )}
      </div>
      {/* A parte do Avatar permanece em Profile.js por enquanto, ou pode ser movida para cá se preferir */}
    </div>
  );
};

export default PersonalInformationSection;
